import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Briefcase, 
  Calculator, 
  MapPin, 
  FileText, 
  Download, 
  ExternalLink, 
  Search, 
  Filter,
  Star,
  Clock,
  Users,
  BookOpen,
  CreditCard,
  Home,
  GraduationCap,
  Shield,
  Globe,
  ChevronRight,
  ArrowRight,
  Plus,
  Heart,
  Zap,
  Target,
  Award,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  Info
} from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { useAuth } from '../contexts/AuthContext';
import { AnimatedCounter } from '../components/ui';
import KnowledgeLibrary from '../components/KnowledgeLibrary';
import InteractiveBudgetCalculator from '../components/InteractiveBudgetCalculator';
import LocalResourceFinder from '../components/LocalResourceFinder';
import DocumentTemplateGenerator from '../components/DocumentTemplateGenerator';

interface Tool {
  id: string;
  title: string;
  description: string;
  category: string;
  icon: React.ElementType;
  color: string;
  type: 'calculator' | 'finder' | 'template' | 'guide' | 'download' | 'external';
  difficulty: 'easy' | 'medium' | 'hard';
  estimatedTime: string;
  popularity: number;
  isNew?: boolean;
  isPremium?: boolean;
  component?: React.ComponentType;
  url?: string;
  tags: string[];
}

interface Resource {
  id: string;
  title: string;
  description: string;
  category: string;
  type: 'guide' | 'template' | 'checklist' | 'video' | 'article';
  icon: React.ElementType;
  color: string;
  downloadUrl?: string;
  externalUrl?: string;
  fileSize?: string;
  format?: string;
  lastUpdated: string;
  rating: number;
  downloads: number;
  tags: string[];
}

const MyToolsResourcesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'tools' | 'resources'>('tools');

  useSEO({
    title: 'My Tools & Resources - MyNewStart',
    description: 'Practical helpers, calculators, templates, local services, and downloads to support your American journey.',
    keywords: ['tools', 'resources', 'calculators', 'templates', 'downloads', 'local services'],
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'Dashboard', url: '/dashboard' },
      { name: 'My Tools & Resources', url: '/library' }
    ]
  });

  const tools: Tool[] = [
    {
      id: 'budget-calculator',
      title: 'Budget Calculator',
      description: 'Plan your finances with our interactive budget calculator. Get personalized tips for managing money in America.',
      category: 'finance',
      icon: Calculator,
      color: 'from-green-500 to-emerald-500',
      type: 'calculator',
      difficulty: 'easy',
      estimatedTime: '10-15 min',
      popularity: 95,
      component: InteractiveBudgetCalculator,
      tags: ['budget', 'finance', 'planning', 'money management']
    },
    {
      id: 'resource-finder',
      title: 'Local Resource Finder',
      description: 'Find local services, organizations, and support groups in your area. Filter by language, cost, and type.',
      category: 'community',
      icon: MapPin,
      color: 'from-blue-500 to-cyan-500',
      type: 'finder',
      difficulty: 'easy',
      estimatedTime: '5-10 min',
      popularity: 88,
      component: LocalResourceFinder,
      tags: ['local', 'services', 'community', 'support']
    },
    {
      id: 'document-generator',
      title: 'Document Templates',
      description: 'Get step-by-step guidance for common forms and documents. SSN, State ID, job applications, and more.',
      category: 'documents',
      icon: FileText,
      color: 'from-purple-500 to-indigo-500',
      type: 'template',
      difficulty: 'medium',
      estimatedTime: '20-30 min',
      popularity: 92,
      component: DocumentTemplateGenerator,
      tags: ['documents', 'forms', 'templates', 'applications']
    },
    {
      id: 'credit-score-simulator',
      title: 'Credit Score Simulator',
      description: 'Understand how different actions affect your credit score with our interactive simulator.',
      category: 'finance',
      icon: TrendingUp,
      color: 'from-orange-500 to-red-500',
      type: 'calculator',
      difficulty: 'medium',
      estimatedTime: '15-20 min',
      popularity: 76,
      isNew: true,
      tags: ['credit', 'score', 'simulation', 'finance']
    },
    {
      id: 'tax-estimator',
      title: 'Tax Estimator',
      description: 'Estimate your tax obligations and plan for tax season with our comprehensive calculator.',
      category: 'finance',
      icon: FileText,
      color: 'from-blue-600 to-blue-800',
      type: 'calculator',
      difficulty: 'hard',
      estimatedTime: '25-35 min',
      popularity: 68,
      isPremium: true,
      tags: ['taxes', 'estimation', 'planning', 'finance']
    },
    {
      id: 'housing-affordability',
      title: 'Housing Affordability Calculator',
      description: 'Calculate how much house you can afford based on your income, expenses, and location.',
      category: 'housing',
      icon: Home,
      color: 'from-teal-500 to-green-500',
      type: 'calculator',
      difficulty: 'easy',
      estimatedTime: '10-15 min',
      popularity: 82,
      tags: ['housing', 'affordability', 'mortgage', 'rent']
    }
  ];

  const resources: Resource[] = [
    {
      id: 'banking-checklist',
      title: 'Banking Setup Checklist',
      description: 'Complete checklist for opening your first U.S. bank account and setting up online banking.',
      category: 'banking',
      type: 'checklist',
      icon: CreditCard,
      color: 'from-blue-500 to-blue-600',
      downloadUrl: '/downloads/banking-checklist.pdf',
      fileSize: '2.1 MB',
      format: 'PDF',
      lastUpdated: '2024-01-15',
      rating: 4.8,
      downloads: 1247,
      tags: ['banking', 'checklist', 'setup', 'account']
    },
    {
      id: 'tax-documents-guide',
      title: 'Essential Tax Documents Guide',
      description: 'Comprehensive guide to understanding and organizing your tax documents.',
      category: 'taxes',
      type: 'guide',
      icon: FileText,
      color: 'from-green-500 to-green-600',
      downloadUrl: '/downloads/tax-documents-guide.pdf',
      fileSize: '3.4 MB',
      format: 'PDF',
      lastUpdated: '2024-01-10',
      rating: 4.9,
      downloads: 892,
      tags: ['taxes', 'documents', 'guide', 'organization']
    },
    {
      id: 'job-application-template',
      title: 'Job Application Template',
      description: 'Professional resume and cover letter templates tailored for the U.S. job market.',
      category: 'career',
      type: 'template',
      icon: Briefcase,
      color: 'from-purple-500 to-purple-600',
      downloadUrl: '/downloads/job-application-template.docx',
      fileSize: '1.8 MB',
      format: 'DOCX',
      lastUpdated: '2024-01-12',
      rating: 4.7,
      downloads: 1563,
      tags: ['career', 'resume', 'template', 'job search']
    },
    {
      id: 'housing-search-checklist',
      title: 'Housing Search Checklist',
      description: 'Step-by-step checklist for finding and securing housing in the U.S.',
      category: 'housing',
      type: 'checklist',
      icon: Home,
      color: 'from-orange-500 to-orange-600',
      downloadUrl: '/downloads/housing-search-checklist.pdf',
      fileSize: '1.5 MB',
      format: 'PDF',
      lastUpdated: '2024-01-08',
      rating: 4.6,
      downloads: 743,
      tags: ['housing', 'search', 'checklist', 'rental']
    },
    {
      id: 'insurance-comparison-guide',
      title: 'Insurance Comparison Guide',
      description: 'Compare different types of insurance and find the best coverage for your needs.',
      category: 'insurance',
      type: 'guide',
      icon: Shield,
      color: 'from-red-500 to-red-600',
      downloadUrl: '/downloads/insurance-comparison-guide.pdf',
      fileSize: '2.8 MB',
      format: 'PDF',
      lastUpdated: '2024-01-05',
      rating: 4.5,
      downloads: 567,
      tags: ['insurance', 'comparison', 'coverage', 'guide']
    },
    {
      id: 'education-pathway-planner',
      title: 'Education Pathway Planner',
      description: 'Plan your educational journey in the U.S. with our comprehensive pathway planner.',
      category: 'education',
      type: 'template',
      icon: GraduationCap,
      color: 'from-indigo-500 to-indigo-600',
      downloadUrl: '/downloads/education-pathway-planner.xlsx',
      fileSize: '2.2 MB',
      format: 'XLSX',
      lastUpdated: '2024-01-03',
      rating: 4.4,
      downloads: 423,
      tags: ['education', 'planning', 'pathway', 'career']
    }
  ];

  const categories = [
    { id: 'all', name: 'All Categories', icon: Globe, color: 'bg-gray-500' },
    { id: 'finance', name: 'Finance & Banking', icon: CreditCard, color: 'bg-blue-500' },
    { id: 'housing', name: 'Housing & Living', icon: Home, color: 'bg-green-500' },
    { id: 'career', name: 'Career & Education', icon: GraduationCap, color: 'bg-purple-500' },
    { id: 'documents', name: 'Documents & Forms', icon: FileText, color: 'bg-orange-500' },
    { id: 'community', name: 'Community & Support', icon: Users, color: 'bg-teal-500' },
    { id: 'insurance', name: 'Insurance', icon: Shield, color: 'bg-red-500' }
  ];

  const filteredTools = tools.filter(tool => {
    const matchesCategory = selectedCategory === 'all' || tool.category === selectedCategory;
    const matchesSearch = searchTerm === '' || 
      tool.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tool.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tool.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const filteredResources = resources.filter(resource => {
    const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
    const matchesSearch = searchTerm === '' || 
      resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'hard': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'calculator': return 'text-blue-600 bg-blue-100';
      case 'finder': return 'text-green-600 bg-green-100';
      case 'template': return 'text-purple-600 bg-purple-100';
      case 'guide': return 'text-orange-600 bg-orange-100';
      case 'download': return 'text-red-600 bg-red-100';
      case 'external': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const handleToolSelect = (toolId: string) => {
    setActiveTool(toolId);
  };

  const handleBackToTools = () => {
    setActiveTool(null);
  };

  if (activeTool) {
    const tool = tools.find(t => t.id === activeTool);
    if (tool && tool.component) {
      const ToolComponent = tool.component;
      return (
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={handleBackToTools}
              className="flex items-center gap-2 text-neutral-600 hover:text-neutral-800 transition-colors"
            >
              <ArrowRight className="w-5 h-5 rotate-180" />
              Back to Tools
            </button>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-primary-50 to-secondary-50 p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 bg-gradient-to-r ${tool.color} rounded-xl flex items-center justify-center`}>
                  <tool.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{tool.title}</h1>
                  <p className="text-gray-600">{tool.description}</p>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <ToolComponent />
            </div>
          </div>
        </div>
      );
    }
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          <Briefcase className="w-10 h-10 inline-block mr-3 text-primary-600" />
          My Tools & Resources
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Practical helpers, calculators, templates, local services, and downloads to support your American journey.
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 text-center">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
            <Calculator className="w-6 h-6 text-blue-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">
            <AnimatedCounter value={tools.length} />
          </div>
          <p className="text-sm text-gray-600">Interactive Tools</p>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 text-center">
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
            <Download className="w-6 h-6 text-green-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">
            <AnimatedCounter value={resources.length} />
          </div>
          <p className="text-sm text-gray-600">Downloadable Resources</p>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 text-center">
          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
            <Users className="w-6 h-6 text-purple-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">
            <AnimatedCounter value={resources.reduce((sum, r) => sum + r.downloads, 0)} />
          </div>
          <p className="text-sm text-gray-600">Total Downloads</p>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 text-center">
          <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-3">
            <Star className="w-6 h-6 text-orange-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">
            {resources.length > 0 ? (resources.reduce((sum, r) => sum + r.rating, 0) / resources.length).toFixed(1) : '0.0'}
          </div>
          <p className="text-sm text-gray-600">Average Rating</p>
        </div>
      </div>

      {/* View Mode Toggle */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('tools')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'tools' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Interactive Tools
          </button>
          <button
            onClick={() => setViewMode('resources')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'resources' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Downloadable Resources
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={`Search ${viewMode === 'tools' ? 'tools' : 'resources'}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">All Categories</option>
            <option value="finance">Finance & Banking</option>
            <option value="housing">Housing & Living</option>
            <option value="career">Career & Education</option>
            <option value="documents">Documents & Forms</option>
            <option value="community">Community & Support</option>
            <option value="insurance">Insurance</option>
          </select>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            className={`flex items-center px-4 py-2 rounded-full border transition-all duration-200 ${
              selectedCategory === category.id
                ? 'bg-primary-100 border-primary-500 text-primary-700'
                : 'bg-white border-gray-300 text-gray-600 hover:border-primary-300 hover:text-primary-600'
            }`}
          >
            <category.icon className="w-4 h-4 mr-2" />
            {category.name}
          </button>
        ))}
      </div>

      {/* Content */}
      {viewMode === 'tools' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTools.map((tool) => (
            <div
              key={tool.id}
              className="group bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden hover:shadow-lg hover:border-primary-300 transition-all duration-300 cursor-pointer"
              onClick={() => handleToolSelect(tool.id)}
            >
              {/* Header */}
              <div className="relative p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 bg-gradient-to-r ${tool.color} rounded-xl flex items-center justify-center`}>
                    <tool.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex gap-2">
                    {tool.isNew && (
                      <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                        New
                      </span>
                    )}
                    {tool.isPremium && (
                      <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2 py-1 rounded-full">
                        Premium
                      </span>
                    )}
                  </div>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-700 transition-colors">
                  {tool.title}
                </h3>

                <p className="text-gray-600 mb-4 line-clamp-2">
                  {tool.description}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-1 mb-4">
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(tool.type)}`}>
                    {tool.type}
                  </span>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(tool.difficulty)}`}>
                    {tool.difficulty}
                  </span>
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    {tool.estimatedTime}
                  </div>
                  <div className="flex items-center">
                    <Star className="w-4 h-4 mr-1" />
                    {tool.popularity}% popular
                  </div>
                </div>

                {/* Action Button */}
                <button className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors duration-200 flex items-center justify-center group">
                  Open Tool
                  <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredResources.map((resource) => (
            <div
              key={resource.id}
              className="group bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden hover:shadow-lg hover:border-primary-300 transition-all duration-300"
            >
              {/* Header */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 bg-gradient-to-r ${resource.color} rounded-xl flex items-center justify-center`}>
                    <resource.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <Star className="w-4 h-4 text-yellow-500 mr-1" />
                    {resource.rating}
                  </div>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-700 transition-colors">
                  {resource.title}
                </h3>

                <p className="text-gray-600 mb-4 line-clamp-2">
                  {resource.description}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-1 mb-4">
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(resource.type)}`}>
                    {resource.type}
                  </span>
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <div className="flex items-center">
                    <Download className="w-4 h-4 mr-1" />
                    {resource.downloads.toLocaleString()} downloads
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    {resource.fileSize}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  {resource.downloadUrl && (
                    <button
                      onClick={() => window.open(resource.downloadUrl, '_blank')}
                      className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors duration-200 flex items-center justify-center"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </button>
                  )}
                  {resource.externalUrl && (
                    <button
                      onClick={() => window.open(resource.externalUrl, '_blank')}
                      className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {(viewMode === 'tools' && filteredTools.length === 0) || (viewMode === 'resources' && filteredResources.length === 0) ? (
        <div className="text-center py-12">
          <Briefcase className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No {viewMode} found</h3>
          <p className="text-gray-600 mb-6">
            Try adjusting your search criteria or explore different categories.
          </p>
          <button
            onClick={() => {
              setSearchTerm('');
              setSelectedCategory('all');
            }}
            className="btn btn-primary"
          >
            Clear Filters
          </button>
        </div>
      ) : null}

      {/* Call to Action */}
      <div className="mt-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl p-8 text-center text-white">
        <h2 className="text-2xl font-bold mb-4">Need More Resources?</h2>
        <p className="text-lg mb-6 opacity-90">
          Can't find what you're looking for? Our community and support team are here to help!
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => navigate('/chat')}
            className="btn bg-white text-primary-600 hover:bg-gray-100"
          >
            Ask Mini Angel
          </button>
          <button
            onClick={() => navigate('/guides')}
            className="btn border-2 border-white text-white hover:bg-white hover:text-primary-600"
          >
            Explore Guides
          </button>
        </div>
      </div>
    </div>
  );
};

export default MyToolsResourcesPage;
